'use memo';

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { AiChatPageContext } from '@/features/global-ai-chat/api/types';

type ContextRegistryEntry = {
  key: string;
  context: AiChatPageContext;
};

type AiChatContextRegistryValue = {
  activeContext: AiChatPageContext | null;
  registerContext: (context: AiChatPageContext) => () => void;
};

const AiChatContextRegistry = createContext<AiChatContextRegistryValue | null>(
  null,
);

export function AiChatContextRegistryProvider({ children }: PropsWithChildren) {
  const [entries, setEntries] = useState<ContextRegistryEntry[]>([]);
  const sequenceRef = useRef(0);

  // setEntries / sequenceRef は安定参照のため、registerContext も安定参照になる。
  // useMemo 内に置くと entries 変更のたびに参照が変わり、購読側 useEffect の
  // クリーンアップ→再登録が無限に走るため、useMemo の外で useCallback として固定する。
  const registerContext = useCallback((context: AiChatPageContext) => {
    const key = `${context.id}:${sequenceRef.current}`;
    sequenceRef.current += 1;
    setEntries((prev) => [...prev, { key, context }]);
    return () => {
      setEntries((prev) => prev.filter((entry) => entry.key !== key));
    };
  }, []);

  const value = useMemo<AiChatContextRegistryValue>(
    () => ({
      activeContext: entries.at(-1)?.context ?? null,
      registerContext,
    }),
    [entries, registerContext],
  );

  return (
    <AiChatContextRegistry.Provider value={value}>
      {children}
    </AiChatContextRegistry.Provider>
  );
}

export function useAiChatContextRegistry() {
  const context = useContext(AiChatContextRegistry);
  if (context) return context;
  return {
    activeContext: null,
    registerContext: () => () => {},
  };
}
